/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import { colors, layout } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import StateService from '../../../../../services/State';
import { buildIT2104Pdf } from '../utils/pdfBuilders';
import { useLivePdfPreview } from '../utils/livePdfPreview';
import { getPreEmploymentLookup } from '../utils/preEmploymentLookup';
import { sanitizeAmountInput, sanitizeInteger } from '../utils/numberValidation';
import DateInput from '../components/DateInput';
import { BaseStepProps } from './BaseStepProps';

const IT2104_PDF = 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/it-2104-form.pdf';

interface IT2104Draft {
  firstNameMI: string;
  lastName: string;
  socialSecurityNumber: string;
  homeAddress: string;
  apartmentNumber: string;
  cityVillagePO: string;
  state: string;
  zipCode: string;
  filingStatus: 'single' | 'married' | 'married_single_rate' | '';
  isNyCityResident: 'yes' | 'no' | '';
  isYonkersResident: 'yes' | 'no' | '';
  allowancesLine1: string;
  allowancesLine2: string;
  nysAmountLine3: string;
  nycAmountLine4: string;
  yonkersAmountLine5: string;
  claimedMoreThan14Exemptions: boolean;
  isNewHireOrRehire: boolean;
  firstDateServicesForPay: string;
  dependentHealthBenefitsAvailable: 'yes' | 'no' | '';
  dateEmployeeQualifiesForBenefits: string;
}

const empty: IT2104Draft = {
  firstNameMI: '',
  lastName: '',
  socialSecurityNumber: '',
  homeAddress: '',
  apartmentNumber: '',
  cityVillagePO: '',
  state: '',
  zipCode: '',
  filingStatus: '',
  isNyCityResident: '',
  isYonkersResident: '',
  allowancesLine1: '0',
  allowancesLine2: '0',
  nysAmountLine3: '',
  nycAmountLine4: '',
  yonkersAmountLine5: '',
  claimedMoreThan14Exemptions: false,
  isNewHireOrRehire: false,
  firstDateServicesForPay: '',
  dependentHealthBenefitsAvailable: '',
  dateEmployeeQualifiesForBenefits: '',
};

const formatSsn = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const radio = (selected: boolean) => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: `2px solid ${selected ? colors.accent : '#9CA3AF'}`,
  position: 'relative' as const,
  flexShrink: 0,
});

const radioInner: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: colors.accent,
};

const IT2104Step = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  application,
  applicationDraft,
  draftDocs,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const backendDraft = draftDocs.find((d) => d.type === 'IT2104');
  const lookup = getPreEmploymentLookup(application, applicationDraft, token);
  const initialFromBackend: IT2104Draft = {
    ...empty,
    firstNameMI: lookup.firstName,
    lastName: lookup.lastName,
    socialSecurityNumber: lookup.ssn,
    homeAddress: lookup.address,
    cityVillagePO: lookup.city,
    state: lookup.state,
    zipCode: lookup.zipCode,
    ...(backendDraft?.options || {}),
  };
  const initial = (draft as IT2104Draft) || initialFromBackend;
  const [data, setData] = useState<IT2104Draft>({ ...empty, ...initial });
  const [states, setStates] = useState<{ uf: string; name: string }[]>([]);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    StateService('')
      .listSelect()
      .then((result: any) => setStates(result?.data || []))
      .catch(() => setStates([]));
  }, []);

  const update = (patch: Partial<IT2104Draft>) => {
    const next = { ...data, ...patch };
    setData(next);
    saveDraft(next);
  };

  const firstDateRequiredFilled = !data.isNewHireOrRehire || !!data.firstDateServicesForPay;
  const dependentDateRequiredFilled =
    data.dependentHealthBenefitsAvailable !== 'yes' || !!data.dateEmployeeQualifiesForBenefits;

  const canOpenSignature =
    !!data.firstNameMI &&
    !!data.lastName &&
    !!data.socialSecurityNumber &&
    !!data.homeAddress &&
    !!data.cityVillagePO &&
    !!data.state &&
    !!data.zipCode &&
    !!data.filingStatus &&
    !!data.isNyCityResident &&
    !!data.isYonkersResident &&
    firstDateRequiredFilled &&
    dependentDateRequiredFilled &&
    !savingDraft;

  const buildFormDataPayload = (signaturePng: string) => ({
    year: moment().format('YYYY'),
    ...data,
    employeeSignature: signaturePng,
    signatureDate: signaturePng ? moment().format('MM/DD/YYYY') : '',
    employerNameAddress: tokenData.school?.name || '',
    employerIdNumber: tokenData.school?.federalTaxId || '',
  });

  const livePdfUrl = useLivePdfPreview({
    builder: async () => (await buildIT2104Pdf(IT2104_PDF, buildFormDataPayload(''), '')) || undefined,
    deps: [data, tokenData.school?.name, tokenData.school?.federalTaxId],
  });

  const submitWithSignature = async (signaturePng: string) => {
    const formData = buildFormDataPayload(signaturePng);
    const pdfBytes = await buildIT2104Pdf(IT2104_PDF, formData, signaturePng);
    const pdfSigned = pdfBytes ? await blobToBase64(new Blob([pdfBytes as any], { type: 'application/pdf' })) : null;

    await HireDocuments('').create({
      type: 'IT2104',
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl: IT2104_PDF,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: {
        ...formData,
        signPng: signaturePng,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    });

    clearDraft();
    toast.success('IT-2104 signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      const formData = buildFormDataPayload('');
      await HireDocuments('').create({
        type: 'IT2104',
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl: IT2104_PDF,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: { ...formData, signPng: '' },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save IT-2104.');
    } finally {
      setSavingDraft(false);
    }
  };

  const yesNo = (value: 'yes' | 'no' | '', onChange: (v: 'yes' | 'no') => void, label: string) => (
    <div style={{ marginBottom: 16 }}>
      <label style={layout.label}>{label} *</label>
      <div style={{ display: 'flex', gap: 12 }}>
        {(['yes', 'no'] as const).map((opt) => (
          <div
            key={opt}
            onClick={() => onChange(opt)}
            style={{
              ...layout.optionCard,
              ...(value === opt ? layout.optionCardSelected : {}),
              flex: 1,
              marginBottom: 0,
            }}
          >
            <div style={radio(value === opt)}>{value === opt && <div style={radioInner} />}</div>
            <span style={{ textTransform: 'capitalize' }}>{opt}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const filingOptions: { value: IT2104Draft['filingStatus']; label: string }[] = [
    { value: 'single', label: 'Single or Head of household' },
    { value: 'married', label: 'Married' },
    { value: 'married_single_rate', label: 'Married, but withhold at higher single rate' },
  ];

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
        Form IT-2104: Employee&apos;s Withholding Allowance Certificate (NY)
      </h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Review the form below, complete the fields and sign at the bottom.
      </p>

      <InlinePdfViewer url={livePdfUrl || IT2104_PDF} maxHeight="55vh" />

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Personal Information</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>First name and middle initial *</label>
          <input
            type="text"
            value={data.firstNameMI}
            onChange={(e) => update({ firstNameMI: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Last name *</label>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => update({ lastName: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Your Social Security Number *</label>
        <input
          type="text"
          value={data.socialSecurityNumber}
          onChange={(e) => update({ socialSecurityNumber: formatSsn(e.target.value) })}
          placeholder="XXX-XX-XXXX"
          style={layout.input}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Home address (number and street) *</label>
          <input
            type="text"
            value={data.homeAddress}
            onChange={(e) => update({ homeAddress: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Apartment number</label>
          <input
            type="text"
            value={data.apartmentNumber}
            onChange={(e) => update({ apartmentNumber: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={layout.label}>City, village, or post office *</label>
          <input
            type="text"
            value={data.cityVillagePO}
            onChange={(e) => update({ cityVillagePO: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>State *</label>
          <select value={data.state} onChange={(e) => update({ state: e.target.value })} style={layout.select}>
            <option value="">Select...</option>
            {states.map((s) => (
              <option key={s.uf} value={s.uf}>
                {s.uf}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={layout.label}>ZIP code *</label>
          <input
            type="text"
            value={data.zipCode}
            onChange={(e) => update({ zipCode: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Filing Status</h4>
      <div style={{ marginBottom: 16 }}>
        {filingOptions.map((opt) => (
          <div
            key={opt.value}
            onClick={() => update({ filingStatus: opt.value })}
            style={{
              ...layout.optionCard,
              ...(data.filingStatus === opt.value ? layout.optionCardSelected : {}),
            }}
          >
            <div style={radio(data.filingStatus === opt.value)}>
              {data.filingStatus === opt.value && <div style={radioInner} />}
            </div>
            {opt.label}
          </div>
        ))}
      </div>

      {yesNo(data.isNyCityResident, (v) => update({ isNyCityResident: v }), 'Are you a resident of New York City?')}
      {yesNo(data.isYonkersResident, (v) => update({ isYonkersResident: v }), 'Are you a resident of Yonkers?')}

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Withholding Allowances</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>1. Total number of allowances for New York State *</label>
          <input
            type="text"
            inputMode="numeric"
            value={data.allowancesLine1}
            onChange={(e) => update({ allowancesLine1: sanitizeInteger(e.target.value) })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>2. Total number of allowances for New York City *</label>
          <input
            type="text"
            inputMode="numeric"
            value={data.allowancesLine2}
            onChange={(e) => update({ allowancesLine2: sanitizeInteger(e.target.value) })}
            style={layout.input}
          />
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Additional Withholding Per Pay Period</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={layout.label}>3. NYS amount</label>
          <input
            type="text"
            inputMode="decimal"
            value={data.nysAmountLine3}
            onChange={(e) => update({ nysAmountLine3: sanitizeAmountInput(e.target.value) })}
            placeholder="$0"
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>4. NYC amount</label>
          <input
            type="text"
            inputMode="decimal"
            value={data.nycAmountLine4}
            onChange={(e) => update({ nycAmountLine4: sanitizeAmountInput(e.target.value) })}
            placeholder="$0"
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>5. Yonkers amount</label>
          <input
            type="text"
            inputMode="decimal"
            value={data.yonkersAmountLine5}
            onChange={(e) => update({ yonkersAmountLine5: sanitizeAmountInput(e.target.value) })}
            placeholder="$0"
            style={layout.input}
          />
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Additional Information</h4>
      <label style={{ ...layout.optionCard, ...(data.claimedMoreThan14Exemptions ? layout.optionCardSelected : {}) }}>
        <input
          type="checkbox"
          checked={data.claimedMoreThan14Exemptions}
          onChange={(e) => update({ claimedMoreThan14Exemptions: e.target.checked })}
        />
        I am claiming more than 14 exemptions on my federal Form W-4
      </label>
      <label style={{ ...layout.optionCard, ...(data.isNewHireOrRehire ? layout.optionCardSelected : {}) }}>
        <input
          type="checkbox"
          checked={data.isNewHireOrRehire}
          onChange={(e) => update({ isNewHireOrRehire: e.target.checked })}
        />
        I am a new hire or rehire (employer reports to NY State New Hire Reporting)
      </label>

      {data.isNewHireOrRehire && (
        <div style={{ marginBottom: 16, marginTop: 8 }}>
          <label style={layout.label}>First date services performed for pay</label>
          <DateInput value={data.firstDateServicesForPay} onChange={(v) => update({ firstDateServicesForPay: v })} />
        </div>
      )}

      {yesNo(
        data.dependentHealthBenefitsAvailable,
        (v) => update({ dependentHealthBenefitsAvailable: v }),
        'Are dependent health insurance benefits available for this employee?',
      )}
      {data.dependentHealthBenefitsAvailable === 'yes' && (
        <div style={{ marginBottom: 16 }}>
          <label style={layout.label}>Date employee qualifies for benefits</label>
          <DateInput
            value={data.dateEmployeeQualifiesForBenefits}
            onChange={(v) => update({ dateEmployeeQualifiesForBenefits: v })}
          />
        </div>
      )}

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title="Sign IT-2104"
        submitLabel="Accept & Sign"
        previewPdfUrl={livePdfUrl}
      />
    </StepShell>
  );
};

export default IT2104Step;
